// frontend/src/components/approver/ApproverMainContent.tsx - WITH APPROVAL REMARKS SUPPORT
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { AuthUser } from '../../services/auth.service';
import { 
  approverService, 
  OvertimeRequest, 
  ApprovalAction, 
  RequestFilters 
} from '../../services/approver.service';

interface ApproverMainContentProps {
  activePage: string;
  onNavigate?: (page: string, params?: any) => void;
  isTransitioning?: boolean;
  isSidebarCollapsed?: boolean;
  user?: AuthUser;
}

const ApproverMainContent: React.FC<ApproverMainContentProps> = ({
  activePage,
  onNavigate,
  isTransitioning = false,
  isSidebarCollapsed = false,
  user
}) => {
  // State management
  const [requests, setRequests] = useState<OvertimeRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<OvertimeRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state - UPDATED
  const [selectedRequest, setSelectedRequest] = useState<OvertimeRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'view' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState<RequestFilters>({
    status: 'all',
    dateFrom: undefined,
    dateTo: undefined,
    projectName: ''
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    todayRequests: 0,
    totalEmployees: 0,
    totalHours: 0
  });

  // Get approver ID
  const approverId = user?.employeeNo || user?.employeeNumber || user?.uid || '';

  // Load data on component mount and filter changes
  useEffect(() => {
    if (approverId) {
      loadRequests();
      loadStatistics();
    }
  }, [approverId, filters]);

  // Apply search filter
  useEffect(() => {
    applySearch();
  }, [requests, searchQuery]);

  // Load requests from service
  const loadRequests = useCallback(async () => {
    if (!approverId) {
      setError('Approver not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const requestsData = await approverService.getOvertimeRequests(approverId, filters);
      setRequests(requestsData);
      
      console.log(`Loaded ${requestsData.length} overtime requests`);
    } catch (err) {
      console.error('Error loading requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setIsLoading(false);
    }
  }, [approverId, filters]);

  // Load statistics
  const loadStatistics = useCallback(async () => {
    if (!approverId) return;

    try {
      const stats = await approverService.getApproverStatistics(approverId);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, [approverId]);

  // Apply search filter
  const applySearch = useCallback(() => {
    let filtered = [...requests];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(request =>
        request.projectName.toLowerCase().includes(query) ||
        request.projectCode.toLowerCase().includes(query) ||
        request.requesterName.toLowerCase().includes(query)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, searchQuery]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof RequestFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // UPDATED: Handle request approval/rejection with remarks
  const handleProcessRequest = useCallback(async (action: 'approve' | 'reject') => {
    if (!selectedRequest || !user) return;

    // Validation: Rejection requires remarks
    if (action === 'reject' && !remarks.trim()) {
      setError('Rejection reason is required');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const approvalAction: ApprovalAction = {
        requestId: selectedRequest.id,
        action,
        reason: remarks.trim() || undefined, // Send remarks for both approve and reject
        approverId: approverId,
        approverName: user.name || 'Approver'
      };

      const result = await approverService.processRequest(approvalAction);
      
      if (result.success) {
        // Refresh data
        await loadRequests();
        await loadStatistics();
        
        // Close modal and reset
        setShowApprovalModal(false);
        setSelectedRequest(null);
        setActionType(null);
        setRemarks('');
        
        console.log(result.message);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error processing request:', err);
      setError(err instanceof Error ? err.message : 'Failed to process request');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedRequest, remarks, approverId, user, loadRequests, loadStatistics]);

  // Format date helper
  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Format time helper
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }, []);

  // Render status badge
  const renderStatusBadge = useCallback((status: string) => {
    const statusStyles = {
      pending: { background: 'var(--approver-gradient-warning)', color: 'white' },
      approved: { background: 'var(--approver-gradient-success)', color: 'white' },
      rejected: { background: 'var(--approver-gradient-error)', color: 'white' }
    };

    const style = statusStyles[status as keyof typeof statusStyles] || statusStyles.pending;

    return (
      <span style={{
        ...style,
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.025em'
      }}>
        {status}
      </span>
    );
  }, []);

  // Check access permission
  if (!user?.hasOvertimeApprovalAccess) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <div>
          <ExclamationTriangleIcon style={{ width: '3rem', height: '3rem', color: '#f56565', margin: '0 auto 1rem' }} />
          <h3 style={{ color: '#dc2626', marginBottom: '1rem' }}>Access Denied</h3>
          <p style={{ color: '#dc2626' }}>
            You do not have permission to approve overtime requests. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Render different pages based on activePage
  const renderContent = () => {
    switch (activePage) {
      case 'dashboard':
        return (
          <div>
            {/* Welcome Section */}
            <div style={{
              background: 'var(--approver-gradient-main)',
              color: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              marginBottom: '2rem',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
            }}>
              <h1 style={{ fontSize: '1.875rem', fontWeight: '700', margin: '0 0 0.5rem 0' }}>
                Welcome back, {user?.name || 'Approver'}!
              </h1>
              <p style={{ fontSize: '1rem', opacity: '0.9', margin: '0 0 0.5rem 0' }}>
                You have {statistics.pending} pending overtime requests to review
              </p>
              <div style={{ 
                fontSize: '0.875rem', 
                opacity: '0.8', 
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                Showing all overtime requests in the system
              </div>
            </div>

            {/* Statistics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    background: 'var(--approver-gradient-success)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                  </div>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: '0 0 0.25rem 0' }}>
                  {statistics.approved}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Approved</div>
              </div>

              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    background: 'var(--approver-gradient-warning)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ClockIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                  </div>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: '0 0 0.25rem 0' }}>
                  {statistics.pending}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Pending</div>
              </div>

              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    background: 'var(--approver-gradient-error)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <XCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                  </div>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: '0 0 0.25rem 0' }}>
                  {statistics.rejected}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Rejected</div>
              </div>

              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '1rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    background: 'var(--approver-gradient-main)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <UserGroupIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                  </div>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: '0 0 0.25rem 0' }}>
                  {statistics.totalEmployees}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Employees</div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '1rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                Quick Actions
              </h2>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => onNavigate?.('all-requests')}
                  style={{
                    background: 'var(--approver-gradient-main)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  <DocumentTextIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  View All Requests ({statistics.total})
                </button>
                
                {statistics.pending > 0 && (
                  <button
                    onClick={() => {
                      setFilters({ ...filters, status: 'pending' });
                      onNavigate?.('all-requests');
                    }}
                    style={{
                      background: 'var(--approver-gradient-warning)',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 4px 12px rgba(237, 137, 54, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(237, 137, 54, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(237, 137, 54, 0.3)';
                    }}
                  >
                    <ClockIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                    Review Pending ({statistics.pending})
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'all-requests':
        return (
          <div>
            {/* Page Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '2rem',
              gap: '1rem'
            }}>
              <div>
                <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                  All Overtime Requests
                </h1>
                <p style={{ color: '#6b7280', fontSize: '1rem', margin: '0' }}>
                  Review and manage all overtime requests in the system
                </p>
              </div>
              
              <button
                onClick={() => {
                  approverService.clearCache();
                  loadRequests();
                  loadStatistics();
                }}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--approver-gradient-main)';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Refresh
              </button>
            </div>

            {/* Filters Section */}
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '1rem',
              marginBottom: '2rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                Filters
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      background: 'white'
                    }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom?.toISOString().split('T')[0] || ''}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : undefined)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo?.toISOString().split('T')[0] || ''}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : undefined)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              {/* Search Bar */}
              <div style={{ marginTop: '1rem', position: 'relative' }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '1.25rem',
                  height: '1.25rem',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  placeholder="Search by project name, code, or requester..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 3rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Requests Table */}
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'var(--approver-gradient-main)',
                color: 'white',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0' }}>
                  Overtime Requests ({filteredRequests.length})
                </h2>
                <div style={{ fontSize: '0.875rem', opacity: '0.9' }}>
                  {statistics.pending} pending • {statistics.approved} approved • {statistics.rejected} rejected
                </div>
              </div>

              {isLoading ? (
                <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid var(--approver-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 1rem'
                  }}></div>
                  <p style={{ color: '#6b7280', margin: '0' }}>Loading requests...</p>
                </div>
              ) : filteredRequests.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                      <tr>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Project Details
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Requester
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Schedule
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Employees
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Status
                        </th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request) => (
                        <tr
                          key={request.id}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                                {request.projectName}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Code: {request.projectCode}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {request.totalHours.toFixed(1)} hours
                              </div>
                            </div>
                          </td>
                          
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            <div>
                              <div style={{ fontWeight: '500' }}>{request.requesterName}</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {formatDate(request.requestTime)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {formatTime(request.requestTime)}
                              </div>
                            </div>
                          </td>
                          
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            <div>
                              <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                                {formatDate(request.startTime)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {formatTime(request.startTime)} - {formatTime(request.endTime)}
                              </div>
                            </div>
                          </td>
                          
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              background: 'linear-gradient(135deg, #9f7aea 0%, #764ba2 100%)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              <UserGroupIcon style={{ width: '1rem', height: '1rem' }} />
                              {request.totalEmployees} employees
                            </div>
                          </td>
                          
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            {renderStatusBadge(request.status)}
                            {request.responseTime && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                {formatDate(request.responseTime)}
                              </div>
                            )}
                          </td>
                          
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', verticalAlign: 'top' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setActionType('view');
                                  setRemarks('');
                                  setShowApprovalModal(true);
                                }}
                                style={{
                                  background: '#f3f4f6',
                                  color: '#374151',
                                  border: '1px solid #d1d5db',
                                  padding: '0.5rem',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'var(--approver-gradient-main)';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.borderColor = 'transparent';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#f3f4f6';
                                  e.currentTarget.style.color = '#374151';
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                }}
                              >
                                <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                                View
                              </button>
                              
                              {request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setActionType('approve');
                                      setRemarks('');
                                      setShowApprovalModal(true);
                                    }}
                                    style={{
                                      background: 'var(--approver-gradient-success)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '0.5rem',
                                      borderRadius: '0.375rem',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(72, 187, 120, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    <CheckCircleIcon style={{ width: '1rem', height: '1rem' }} />
                                    Approve
                                  </button>
                                  
                                  <button
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setActionType('reject');
                                      setRemarks('');
                                      setShowApprovalModal(true);
                                    }}
                                    style={{
                                      background: 'var(--approver-gradient-error)',
                                      color: 'white',
                                      border: 'none',
                                      padding: '0.5rem',
                                      borderRadius: '0.375rem',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 101, 101, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                  <DocumentTextIcon style={{ width: '4rem', height: '4rem', color: '#d1d5db', margin: '0 auto 1rem' }} />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    No Requests Found
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#6b7280', margin: '0' }}>
                    {searchQuery || filters.status !== 'all' 
                      ? 'No requests match your current filters.'
                      : 'No overtime requests are available to review.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            background: 'white',
            borderRadius: '1rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e2e8f0',
            margin: '2rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: '#1f2937', marginBottom: '1rem' }}>
                Coming Soon
              </h2>
              <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
                This feature is under development
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <main style={{
      flex: 1,
      background: 'rgba(248, 250, 252, 0.8)',
      minHeight: 'calc(100vh - 60px)',
      width: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '2rem 1rem',
      opacity: isTransitioning ? 0.8 : 1,
      transition: 'opacity 0.3s ease'
    }}>
      {renderContent()}

      {/* UPDATED: Approval/Rejection Modal with Remarks Support */}
      {showApprovalModal && selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '600px',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            {/* Modal Header */}
            <div style={{
              background: actionType === 'approve' ? 'var(--approver-gradient-success)' :
                          actionType === 'reject' ? 'var(--approver-gradient-error)' :
                          'var(--approver-gradient-main)',
              color: 'white',
              padding: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0' }}>
                {actionType === 'approve' ? 'Approve Overtime Request' : 
                 actionType === 'reject' ? 'Reject Overtime Request' : 
                 'Review Overtime Request'}
              </h3>
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setActionType(null);
                  setRemarks('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  transition: 'all 0.2s ease',
                  fontSize: '1.5rem',
                  lineHeight: '1'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Request Details */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
                border: '1px solid #bbf7d0',
                borderRadius: '0.75rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Project</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                      {selectedRequest.projectName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                      Code: {selectedRequest.projectCode}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Schedule</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                      {formatDate(selectedRequest.startTime)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                      {formatTime(selectedRequest.startTime)} - {formatTime(selectedRequest.endTime)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Duration</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                      {selectedRequest.totalHours.toFixed(1)} hours
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Requester</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                      {selectedRequest.requesterName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                      {formatDate(selectedRequest.requestTime)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Selected Employees */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
                    Selected Employees ({selectedRequest.totalEmployees})
                  </h4>
                  <button
                    onClick={() => {
                      // Open employee details in new tab
                      const employeeData = selectedRequest.employeeDetails;
                      const requestData = selectedRequest;
                      
                      // Create HTML for new tab
                      const htmlContent = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Overtime Request - Employee Details</title>
                          <style>
                            * {
                              margin: 0;
                              padding: 0;
                              box-sizing: border-box;
                            }
                            body {
                              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                              padding: 2rem;
                              background: #f9fafb;
                              color: #1f2937;
                            }
                            .container {
                              max-width: 1200px;
                              margin: 0 auto;
                              background: white;
                              border-radius: 1rem;
                              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                              overflow: hidden;
                            }
                            .header {
                              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                              color: white;
                              padding: 2rem;
                            }
                            .header h1 {
                              font-size: 1.875rem;
                              margin-bottom: 0.5rem;
                            }
                            .header p {
                              opacity: 0.9;
                              font-size: 1rem;
                            }
                            .info-section {
                              padding: 2rem;
                              background: linear-gradient(135deg, #f0fdf4, #f7fee7);
                              border-bottom: 1px solid #bbf7d0;
                            }
                            .info-grid {
                              display: grid;
                              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                              gap: 1.5rem;
                            }
                            .info-item {
                              padding: 1rem;
                              background: white;
                              border-radius: 0.5rem;
                              border: 1px solid #d1fae5;
                            }
                            .info-label {
                              font-size: 0.875rem;
                              color: #059669;
                              font-weight: 600;
                              margin-bottom: 0.25rem;
                            }
                            .info-value {
                              font-size: 1.125rem;
                              color: #047857;
                              font-weight: 700;
                            }
                            .info-sub {
                              font-size: 0.875rem;
                              color: #059669;
                              margin-top: 0.25rem;
                            }
                            .table-container {
                              padding: 2rem;
                            }
                            .table-header {
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                              margin-bottom: 1.5rem;
                            }
                            .table-header h2 {
                              font-size: 1.5rem;
                              color: #1f2937;
                            }
                            .print-btn {
                              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                              color: white;
                              border: none;
                              padding: 0.75rem 1.5rem;
                              border-radius: 0.5rem;
                              font-size: 0.875rem;
                              font-weight: 600;
                              cursor: pointer;
                              box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                              transition: all 0.3s ease;
                            }
                            .print-btn:hover {
                              transform: translateY(-2px);
                              box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
                            }
                            table {
                              width: 100%;
                              border-collapse: collapse;
                              background: white;
                              border-radius: 0.5rem;
                              overflow: hidden;
                              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                            }
                            thead {
                              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                              color: white;
                            }
                            th {
                              padding: 1rem;
                              text-align: left;
                              font-weight: 600;
                              font-size: 0.875rem;
                              text-transform: uppercase;
                              letter-spacing: 0.05em;
                            }
                            tbody tr {
                              border-bottom: 1px solid #f3f4f6;
                              transition: background-color 0.2s ease;
                            }
                            tbody tr:hover {
                              background-color: #f9fafb;
                            }
                            tbody tr:last-child {
                              border-bottom: none;
                            }
                            td {
                              padding: 1rem;
                              font-size: 0.875rem;
                              color: #374151;
                            }
                            .emp-name {
                              font-weight: 600;
                              color: #1f2937;
                            }
                            .emp-number {
                              font-family: 'Courier New', monospace;
                              background: #f3f4f6;
                              padding: 0.25rem 0.5rem;
                              border-radius: 0.25rem;
                              font-weight: 500;
                            }
                            .footer {
                              padding: 1.5rem 2rem;
                              background: #f9fafb;
                              border-top: 1px solid #e5e7eb;
                              text-align: center;
                              color: #6b7280;
                              font-size: 0.875rem;
                            }
                            @media print {
                              body {
                                padding: 0;
                                background: white;
                              }
                              .print-btn {
                                display: none;
                              }
                              .container {
                                box-shadow: none;
                              }
                            }
                          </style>
                        </head>
                        <body>
                          <div class="container">
                            <div class="header">
                              <h1>Overtime Request - Employee Details</h1>
                              <p>Detailed information about selected employees for overtime work</p>
                            </div>
                            
                            <div class="info-section">
                              <div class="info-grid">
                                <div class="info-item">
                                  <div class="info-label">Project</div>
                                  <div class="info-value">${requestData.projectName}</div>
                                  <div class="info-sub">Code: ${requestData.projectCode}</div>
                                </div>
                                <div class="info-item">
                                  <div class="info-label">Schedule</div>
                                  <div class="info-value">${formatDate(requestData.startTime)}</div>
                                  <div class="info-sub">${formatTime(requestData.startTime)} - ${formatTime(requestData.endTime)}</div>
                                </div>
                                <div class="info-item">
                                  <div class="info-label">Duration</div>
                                  <div class="info-value">${requestData.totalHours.toFixed(1)} hours</div>
                                </div>
                                <div class="info-item">
                                  <div class="info-label">Requester</div>
                                  <div class="info-value">${requestData.requesterName}</div>
                                  <div class="info-sub">${formatDate(requestData.requestTime)}</div>
                                </div>
                                <div class="info-item">
                                  <div class="info-label">Status</div>
                                  <div class="info-value" style="text-transform: uppercase;">${requestData.status}</div>
                                </div>
                                <div class="info-item">
                                  <div class="info-label">Total Employees</div>
                                  <div class="info-value">${requestData.totalEmployees}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div class="table-container">
                              <div class="table-header">
                                <h2>Selected Employees (${employeeData.length})</h2>
                                <button class="print-btn" onclick="window.print()">
                                  🖨️ Print / Save as PDF
                                </button>
                              </div>
                              
                              <table>
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Employee Name</th>
                                    <th>PIN / Employee Number</th>
                                    <th>Designation</th>
                                    <th>Department</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${employeeData.map((emp, idx) => `
                                    <tr>
                                      <td>${idx + 1}</td>
                                      <td class="emp-name">${emp.name}</td>
                                      <td><span class="emp-number">${emp.pin || emp.employeeNumber}</span></td>
                                      <td>${emp.designation}</td>
                                      <td>${emp.department}</td>
                                    </tr>
                                  `).join('')}
                                </tbody>
                              </table>
                            </div>
                            
                            <div class="footer">
                              <p>Generated on ${new Date().toLocaleString('en-US', { 
                                dateStyle: 'full', 
                                timeStyle: 'short' 
                              })}</p>
                              <p style="margin-top: 0.5rem;">Phoenician Technical Services LLC - Overtime Management System</p>
                            </div>
                          </div>
                        </body>
                        </html>
                      `;
                      
                      // Open in new tab
                      const newTab = window.open('', '_blank');
                      if (newTab) {
                        newTab.document.write(htmlContent);
                        newTab.document.close();
                      } else {
                        alert('Please allow popups to view employee details');
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                    </svg>
                    View in New Tab
                  </button>
                </div>
                <div style={{ 
                  overflowX: 'auto',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>#</th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>Employee Name</th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>PIN / Employee No.</th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>Designation</th>
                        <th style={{ 
                          padding: '1rem', 
                          textAlign: 'left', 
                          fontWeight: '600',
                          fontSize: '0.875rem'
                        }}>Department</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRequest.employeeDetails.map((employee, index) => (
                        <tr 
                          key={employee.id || index}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <td style={{ 
                            padding: '1rem',
                            fontWeight: '600',
                            color: '#6b7280'
                          }}>
                            {index + 1}
                          </td>
                          <td style={{ 
                            padding: '1rem',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            {employee.name}
                          </td>
                          <td style={{ 
                            padding: '1rem',
                            color: '#374151'
                          }}>
                            <span style={{
                              background: '#f3f4f6',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontFamily: 'monospace',
                              fontWeight: '500'
                            }}>
                              {employee.pin || employee.employeeNumber}
                            </span>
                          </td>
                          <td style={{ 
                            padding: '1rem',
                            color: '#374151'
                          }}>
                            {employee.designation}
                          </td>
                          <td style={{ 
                            padding: '1rem',
                            color: '#374151'
                          }}>
                            {employee.department}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Show existing remarks if viewing processed request */}
              {actionType === 'view' && selectedRequest.status !== 'pending' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    background: selectedRequest.status === 'approved' ? '#f0fdf4' : '#fee2e2',
                    border: selectedRequest.status === 'approved' ? '1px solid #bbf7d0' : '1px solid #fecaca',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '600', 
                      color: selectedRequest.status === 'approved' ? '#047857' : '#dc2626',
                      marginBottom: '0.5rem'
                    }}>
                      {selectedRequest.status === 'approved' ? 'Approval Remarks' : 'Rejection Reason'}
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: selectedRequest.status === 'approved' ? '#065f46' : '#991b1b'
                    }}>
                      {selectedRequest.responseMessage || 'No remarks provided'}
                    </div>
                    {selectedRequest.actualApproverName && (
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#6b7280',
                        marginTop: '0.5rem'
                      }}>
                        By: {selectedRequest.actualApproverName}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* UPDATED: Remarks Input - For both Approve and Reject */}
              {selectedRequest.status === 'pending' && actionType !== 'view' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    {actionType === 'reject' ? 'Rejection Reason (Required)' : 'Approval Remarks (Optional)'}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder={
                      actionType === 'reject' 
                        ? 'Please provide a reason for rejecting this request...'
                        : 'Add any remarks or notes for this approval (optional)...'
                    }
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  {actionType === 'reject' && (
                    <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                      * Rejection reason is mandatory
                    </div>
                  )}
                  {actionType === 'approve' && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      You can add optional remarks to provide context or instructions
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  color: '#dc2626',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            {selectedRequest.status === 'pending' && actionType !== 'view' && (
              <div style={{
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                background: '#f9fafb'
              }}>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedRequest(null);
                    setActionType(null);
                    setRemarks('');
                  }}
                  disabled={isProcessing}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                
                {actionType === 'reject' && (
                  <button
                    onClick={() => handleProcessRequest('reject')}
                    disabled={isProcessing || !remarks.trim()}
                    style={{
                      background: isProcessing || !remarks.trim() 
                        ? '#9ca3af' 
                        : 'var(--approver-gradient-error)',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isProcessing || !remarks.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: isProcessing || !remarks.trim() ? 0.5 : 1
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <div style={{
                          width: '1rem',
                          height: '1rem',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Rejecting...
                      </>
                    ) : (
                      <>
                        <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                        Reject Request
                      </>
                    )}
                  </button>
                )}
                
                {actionType === 'approve' && (
                  <button
                    onClick={() => handleProcessRequest('approve')}
                    disabled={isProcessing}
                    style={{
                      background: isProcessing 
                        ? '#9ca3af' 
                        : 'var(--approver-gradient-success)',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: isProcessing ? 0.5 : 1
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <div style={{
                          width: '1rem',
                          height: '1rem',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon style={{ width: '1rem', height: '1rem' }} />
                        Approve Request
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* View-only close button */}
            {actionType === 'view' && (
              <div style={{
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f9fafb'
              }}>
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedRequest(null);
                    setActionType(null);
                    setRemarks('');
                  }}
                  style={{
                    background: 'var(--approver-gradient-main)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          padding: '1rem',
          maxWidth: '400px',
          zIndex: 1001,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <ExclamationTriangleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#dc2626', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '0.25rem' }}>
                Error
              </div>
              <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                {error}
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#dc2626',
                cursor: 'pointer',
                padding: '0.25rem',
                fontSize: '1.25rem',
                lineHeight: '1'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </main>
  );
};

export default ApproverMainContent;